import { Recapt } from '../engine';
import type { RecordModule } from '../modules/record';
import { Namespace } from '../modules/types';
import { isSuccessResponse } from '../utils/requestUtils';
import {
  ABORT_SYNC_AFTER_MILLIS,
  PURGE_DATA_AFTER_SYNC_IN_A_ROW_FAILS,
  SESSION_DEAD_INTERVAL,
  SESSION_PING_INTERVAL,
  SHUTDOWN_AFTER_SYNC_IN_A_ROW_FAILS,
} from './constants';
import {
  LAST_REQUEST_TIME_SESSION_STORAGE_KEY,
  OBFUSCATION_SELECTOR_STORAGE_KEY,
  SESSION_ID_KEY,
} from './storageConstants';
import { StorageManager } from './storageManager';

type SyncRequestBody = Partial<
  Record<
    | Namespace
    | 'identity'
    | 'painpointMode'
    | 'potentialPainpoint'
    | 'saveSession'
    | 'requestOptions',
    unknown
  >
>;

/**
 * Utility function to create a promise that resolves after a specified time
 * @param ms - The number of milliseconds to sleep
 * @returns Promise that resolves after the specified time
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Manages API requests and synchronization with the server.
 * Handles polling, data synchronization, and request management.
 */
export class RequestManager {
  public static polling = false;

  private static pollingInterval = 1000;

  private static pollingTimeout: null | NodeJS.Timeout = null;

  private static isActive = false;

  private static nextRequestPotentiallyIncludesPainpoint = false;

  private static baseApiUrl: string =
    process.env.RECAPT_API_URL ?? 'https://api.recapt.app/v1beta';

  public static painpointMode: 'system' | 'manual' = 'manual';
  public static saveSession = false;

  private static syncRequestFailsInARow = 0;
  private static lastSyncRequestTime = 0;

  //this should only be used internally for debugging purposes
  public static debugNetworkPrintCallback: null | ((data: any) => void) = null;

  private static lastRequestTime = -1;

  private static hasSynced = false;

  public static getPollingInterval() {
    return this.pollingInterval;
  }

  /**
   * Marks the next request as potentially including a painpoint.
   */
  public static potentialPainpoint() {
    if (this.nextRequestPotentiallyIncludesPainpoint) return;
    this.nextRequestPotentiallyIncludesPainpoint = true;
  }

  /**
   * Starts the polling mechanism for synchronization.
   */
  public static start() {
    if (this.polling) return;
    this.polling = true;

    const savedLastRequestTime = window.sessionStorage.getItem(
      LAST_REQUEST_TIME_SESSION_STORAGE_KEY,
    );

    if (savedLastRequestTime != null) {
      this.lastRequestTime = parseInt(savedLastRequestTime, 10);
      if (!this.lastRequestTime || isNaN(this.lastRequestTime)) {
        this.lastRequestTime = -1;
      }
    }

    this.doPoll();
  }

  /**
   * Stops the polling mechanism and clears any pending timeouts.
   */
  public static stop() {
    if (!this.polling) return;
    this.polling = false;
    this.isActive = false;

    if (this.pollingTimeout) clearTimeout(this.pollingTimeout);

    this.pollingTimeout = null;

    window.sessionStorage.setItem(
      LAST_REQUEST_TIME_SESSION_STORAGE_KEY,
      this.lastRequestTime.toString(),
    );
  }

  /**
   * Sends a synchronization request to the server.
   * @param body - The request body to send
   */
  private static async sendSyncRequest(
    body: SyncRequestBody,
    hasDataToSync: boolean,
  ): Promise<boolean | void> {
    if (!this.isActive) return;

    if (this.debugNetworkPrintCallback !== null) {
      this.debugNetworkPrintCallback(body);
      return true;
    }

    const result = await this.handleSyncRequest(body);

    if (result) {
      this.lastRequestTime = Date.now();
      if (!hasDataToSync) {
        StorageManager.purgeModuleStates(); //this is correct because we dont want under any circumstances to sync the same data twice
      }

      if (
        'obfuscationClasses' in body &&
        Array.isArray(body.obfuscationClasses)
      ) {
        const selector = body.obfuscationClasses.join(',');
        StorageManager.setItem(OBFUSCATION_SELECTOR_STORAGE_KEY, selector);
        Recapt.getModule<RecordModule>('record')?.updateRrRecordSettings({
          maskTextSelector: selector,
        });
      } else {
        //fine to not update the record module settings immediately,
        //it will be updated soon enough. (creates no harm in obfuscating the previous classes for one page load & we dont like to re-create the whole record)
        StorageManager.removeItem(OBFUSCATION_SELECTOR_STORAGE_KEY);
      }
    }

    return result;
  }

  private static async handleSyncRequest(
    body: SyncRequestBody,
    tries = 0,
  ): Promise<boolean> {
    if (tries > 0) {
      let hasArrayValue = false;

      for (const value of Object.values(body)) {
        if (typeof value === 'object' && value != null) {
          if (
            'state' in value &&
            (value as { state: any[] }).state.length > 0
          ) {
            hasArrayValue = true;
            break;
          }
        }
      }

      if (!hasArrayValue) return true;
    }

    const result = await this.doSendSyncRequest(JSON.stringify(body));

    if (result.canRetry === false) return result.success;

    if (result.success === true) return true;

    if (tries >= 2) return false;

    let newBodies: SyncRequestBody[] = [{}, {}];

    for (const [key, value] of Object.entries(body)) {
      if (typeof value === 'object' && value != null) {
        if (!('state' in value)) {
          newBodies[0][key as keyof SyncRequestBody] = value;
          newBodies[1][key as keyof SyncRequestBody] = value;
          continue;
        }

        let state = value.state;

        if (!Array.isArray(state) || state.length === 0) {
          newBodies[0][key as keyof SyncRequestBody] = value;
          newBodies[1][key as keyof SyncRequestBody] = value;
          continue;
        }

        if (state.length === 1) {
          newBodies[0][key as keyof SyncRequestBody] = value;
          continue;
        }

        const midIndex = Math.floor(state.length / 2);

        newBodies[0][key as keyof SyncRequestBody] = {
          ...value,
          state: state.slice(0, midIndex),
        };

        newBodies[1][key as keyof SyncRequestBody] = {
          ...value,
          state: state.slice(midIndex),
        };
      } else {
        newBodies[0][key as keyof SyncRequestBody] = value;
        newBodies[1][key as keyof SyncRequestBody] = value;
      }
    }

    const res = await Promise.all(
      newBodies.map((body) => this.handleSyncRequest(body, tries + 1)),
    );

    return !res.some((r) => r === false);
  }

  private static async doSendSyncRequest(body: string): Promise<{
    success: boolean;
    canRetry?: boolean;
  }> {
    let data: Record<string, any> | null = null;
    let statusCode: number | null = null;

    try {
      const controller = new AbortController();
      const signal = controller.signal;

      const abortTimeout = setTimeout(() => {
        controller.abort();
      }, ABORT_SYNC_AFTER_MILLIS);

      const response = await fetch(`${this.baseApiUrl}/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Identifier-Token': Recapt.apiKey || '',
          ...(Recapt.sessionId ? { 'X-Session-Id': Recapt.sessionId } : {}),
        },
        body,
        signal,
      });

      this.lastSyncRequestTime = Date.now();

      clearTimeout(abortTimeout);

      statusCode = response.status;
      data = (await response.json()) as { newSession?: boolean; id?: string };
    } catch (err) {}

    if (!statusCode || !data) {
      Recapt.purge();
      Recapt.stop();
      return {
        success: false,
        canRetry: false,
      };
    }

    if (!isSuccessResponse(statusCode)) {
      if (statusCode === 401 || statusCode === 403) {
        Recapt.purge();
        Recapt.stop();
        throw new Error('Unauthorized');
      } else if (statusCode === 400) {
        const isMissingApiKey = data.errors.some(
          (error: { message: string; field?: string }) =>
            error.field === 'x-identifier-token',
        );

        if (isMissingApiKey) {
          Recapt.purge();
          Recapt.stop();
          throw new Error('Missing api key');
        }
      }

      if (statusCode >= 500 && statusCode <= 599) {
        Recapt.purge();
        Recapt.stop();
        return {
          success: false,
          canRetry: false,
        };
      }

      if (statusCode >= 400 && statusCode <= 499) {
        return {
          success: false,
          canRetry: true,
        };
      }

      return {
        success: false,
        canRetry: true,
      };
    }

    if ('id' in data && data.id != null && data.id !== Recapt.sessionId) {
      window.sessionStorage.setItem(SESSION_ID_KEY, data.id);
      Recapt.sessionId = data.id;
    }

    if ('newSession' in data && data.newSession === true) {
      Recapt.debug('new session');
      for (const mod of Recapt.modules) {
        mod.onNewSession();
      }
    }

    return {
      success: true,
    };
  }

  /**
   * Sends a termination request to the server.
   */
  public static async sendTerminateRequest() {
    if (!this.isActive) return;
    await fetch(`${this.baseApiUrl}/terminate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Forces an immediate synchronization with the server.
   */
  public static async forceSync() {
    const { data, hasDataToSync } = this.compileSyncRequest();
    await this.sendSyncRequest(data, hasDataToSync);
  }

  /**
   * Compiles data from all modules into a sync request payload.
   * @returns The compiled request data
   */
  private static compileSyncRequest() {
    const data: SyncRequestBody = {};

    if (this.hasSynced) {
      //we'll first send an empty sync request to confirm session state from backend
      //we have systems in place in the inspector to handle this, but these can probably fail
      //at the moment, better safe than sorry
      for (const mod of Recapt.modules) {
        if (!mod.isReady) continue;

        const state = mod.preBuildRequest();
        if (state.length === 0) continue;

        const requestData = mod.buildRequest(state);

        //TODO:
        //.getRequestNamespace() is a hotfix to support both rrweb and axos
        //will be removed once axos is finished
        data[mod.getRequestNamespace() as Namespace] = requestData;
      }
    } else {
      data.requestOptions = true;
    }

    const hasDataToSync = !(Object.keys(data).length === 0);

    data.identity = Recapt.session.getIdentity();
    data.painpointMode = this.painpointMode;
    data.potentialPainpoint = this.nextRequestPotentiallyIncludesPainpoint;

    if (this.painpointMode === 'manual' && this.saveSession) {
      data.saveSession = true;
    }

    this.nextRequestPotentiallyIncludesPainpoint = false;

    return { data, hasDataToSync };
  }

  /**
   * Sets up the next polling iteration.
   */
  private static doPoll() {
    if (!this.polling || this.pollingTimeout !== null) return;

    const that = this;

    this.pollingTimeout = setTimeout(() => {
      if (!that.polling) return;

      that.pollingTimeout = null;
      that.onPoll();
    }, this.pollingInterval);
  }

  /**
   * Handles the polling iteration by checking active state and sending sync requests.
   */
  private static async onPoll() {
    //we'll just poll until we reach a state where we can sync
    if (!this.isActive) {
      if (Recapt.apiKey != null && Recapt.session.ready()) {
        Recapt.debug('request manager reached active state');
        this.isActive = true;
      }

      if (!this.isActive) {
        this.doPoll();
        return;
      }
    }

    let didPurge = false;

    if (
      this.lastRequestTime !== -1 &&
      Date.now() - this.lastRequestTime >= SESSION_DEAD_INTERVAL
    ) {
      Recapt.debug('detected dead session, purging all data');
      this.lastRequestTime = -1;
      this.hasSynced = false;
      Recapt.session.destroy();
      Recapt.reset();

      // reset() restarts polling via pause/resume, so return to avoid
      // continuing in this stale poll cycle
      return;
    }

    const { data, hasDataToSync } = this.compileSyncRequest();

    const sendEmptySyncRequest =
      !this.hasSynced ||
      didPurge ||
      Date.now() - this.lastSyncRequestTime >= SESSION_PING_INTERVAL;

    if (hasDataToSync === false && !sendEmptySyncRequest) {
      this.doPoll();
      return;
    }

    const result = await this.sendSyncRequest(data, hasDataToSync);

    this.hasSynced = true;

    if (result === false) {
      this.syncRequestFailsInARow++;

      if (this.syncRequestFailsInARow >= PURGE_DATA_AFTER_SYNC_IN_A_ROW_FAILS) {
        Recapt.purge();
      }

      if (this.syncRequestFailsInARow >= SHUTDOWN_AFTER_SYNC_IN_A_ROW_FAILS) {
        Recapt.stop();
      }

      //handle retry of sync request if we want to
    } else this.syncRequestFailsInARow = 0;

    this.doPoll();
  }
}
